import React, { useState, useEffect } from 'react';
import { doc, setDoc, updateDoc, onSnapshot, Timestamp, serverTimestamp } from 'firebase/firestore';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs from 'dayjs';
import { getFirestoreDb } from '../../config/config';
import { useAuth } from '../../context/AuthContext';
import Loader from '../common/Loader';
import './Maintenance.css';

const DATE_FORMAT = 'DD/MM/YYYY HH:mm';

const formatTimestamp = (val) => {
  if (!val) return '—';
  if (val.toDate) return dayjs(val.toDate()).format(DATE_FORMAT);
  const d = dayjs(val);
  return d.isValid() ? d.format(DATE_FORMAT) : String(val);
};

const DEFAULT_PRE_MESSAGES = {
  en: 'Scheduled maintenance is coming soon. Please save your work. We apologize for any inconvenience.',
  fr: 'Un entretien planifié aura lieu bientôt. Veuillez sauvegarder votre travail. Nous nous excusons pour tout inconvénient.',
};

const DEFAULT_MESSAGES = {
  en: 'We are currently performing scheduled maintenance. Service will be restored shortly. Thank you for your patience.',
  fr: 'Nous effectuons actuellement un entretien planifié. Le service sera rétabli sous peu. Merci de votre patience.',
};


const pickerSlotProps = {
  textField: {
    fullWidth: true,
    size: 'small',
    sx: {
      width: '100%',
      '& .MuiPickersOutlinedInput-root': {
        color: 'white'
      },
      '& .MuiOutlinedInput-root': {
        backgroundColor: 'rgba(240, 103, 11, 0.3)',
        borderRadius: '12px',
        color: 'white',
        '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.1)' },
        '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
        '&.Mui-focused fieldset': { borderColor: '#f97316', borderWidth: '1px' },
      },
      '& .MuiInputLabel-root': {
        color: 'rgba(246, 135, 7, 0.8)',
        '&.Mui-focused': { color: '#f97316' },
      },
      '& .MuiOutlinedInput-input': { 
        color: 'white',
        '&::placeholder': { color: 'rgba(156, 163, 175, 0.6)', opacity: 1 },
      },
      '& .MuiInputBase-input': { color: 'white !important' },
      '& .MuiIconButton-root': { color: 'rgba(156, 163, 175, 0.8)' },
      '& .MuiFormHelperText-root': { color: 'rgba(156, 163, 175, 0.6)', marginLeft: 0 },
    },
  },
  popper: {
    sx: {
      '& .MuiPaper-root': {
        backgroundColor: '#1a1a2e',
        color: 'white',
        border: '1px solid #374151',
      },
      '& .MuiPickersDay-root': { color: 'white' },
      '& .MuiPickersDay-root.Mui-selected': { backgroundColor: '#f97316' },
      '& .MuiPickersDay-root:hover': { backgroundColor: '#252542' },
      '& .MuiDayCalendar-weekDayLabel': { color: '#9ca3af' },
      '& .MuiPickersCalendarHeader-label': { color: 'white' },
      '& .MuiPickersArrowSwitcher-button': { color: '#9ca3af' },
      '& .MuiClock-pin, & .MuiClockPointer-root': { backgroundColor: '#f97316' },
      '& .MuiClockPointer-thumb': { borderColor: '#f97316' },
      '& .MuiClockNumber-root': { color: 'white' },
      '& .MuiMultiSectionDigitalClock-root': { color: 'white' },
      '& .MuiMenuItem-root.Mui-selected': { backgroundColor: 'rgba(249,115,22,0.2)' },
      '& .MuiDialogActions-root .MuiButton-root': { color: '#f97316' },
    },
  },
};

const Maintenance = () => {
  const { user, environment } = useAuth();
  const [maintenance, setMaintenance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const [form, setForm] = useState({
    preMessageEn: DEFAULT_PRE_MESSAGES.en,
    preMessageFr: DEFAULT_PRE_MESSAGES.fr,
    messageEn: DEFAULT_MESSAGES.en,
    messageFr: DEFAULT_MESSAGES.fr,
    startTime: dayjs(),
    endTime: null,
  });

  useEffect(() => {
    setLoading(true);
    setError('');
    const db = getFirestoreDb();
    const docRef = doc(db, 'maintenance', 'current');

    const unsubscribe = onSnapshot(
      docRef,
      (snap) => {
        if (snap.exists()) {
          setMaintenance({ id: snap.id, ...snap.data() });
        } else {
          setMaintenance(null);
        }
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [environment]);

  const handleEnableMaintenance = async () => {
    if (!form.preMessageEn.trim() || !form.preMessageFr.trim()) {
      setError('Both English and French pre-maintenance messages are required.');
      return;
    }
    if (!form.messageEn.trim() || !form.messageFr.trim()) {
      setError('Both English and French during-maintenance messages are required.');
      return;
    }
    if (!form.endTime || !form.endTime.isValid()) {
      setError('End time is required.');
      return;
    }
    if (!form.startTime || !form.startTime.isValid()) {
      setError('Start time is required.');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const db = getFirestoreDb();
      const docRef = doc(db, 'maintenance', 'current');
      await setDoc(docRef, {
        active: true,
        startTime: Timestamp.fromDate(form.startTime.toDate()),
        endTime: Timestamp.fromDate(form.endTime.toDate()),
        preMessage: {
          en: form.preMessageEn.trim(),
          fr: form.preMessageFr.trim(),
        },
        message: {
          en: form.messageEn.trim(),
          fr: form.messageFr.trim(),
        },
        createdBy: user?.email || 'unknown',
        endedEarly: false,
        endedAt: null,
      });
      setShowForm(false);
      setForm({ preMessageEn: DEFAULT_PRE_MESSAGES.en, preMessageFr: DEFAULT_PRE_MESSAGES.fr, messageEn: DEFAULT_MESSAGES.en, messageFr: DEFAULT_MESSAGES.fr, startTime: dayjs(), endTime: null });
    } catch (err) {
      setError(err.message);
    }
    setSaving(false);
  };

  const handleEndMaintenance = () => {
    setShowConfirmModal(true);
  };

  const confirmEndMaintenance = async () => {
    setShowConfirmModal(false);
    setSaving(true);
    setError('');
    try {
      const db = getFirestoreDb();
      const docRef = doc(db, 'maintenance', 'current');
      await updateDoc(docRef, {
        active: false,
        endedEarly: true,
        endedAt: serverTimestamp(),
      });
    } catch (err) {
      setError(err.message);
    }
    setSaving(false);
  };

  const handleFormChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  if (loading) return <Loader />;

  const isActive = maintenance?.active === true;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <div className="maintenance-page">
        <div className="page-header">
          <h2 className="page-title">Maintenance</h2>
        </div>

        {error && <div className="error-message">{error}</div>}

        {/* Status Card */}
        <div className="settings-card maintenance-status-card">
          <div className="maintenance-status-header">
            <h3 className="card-title">Current Status</h3>
            <span className={`maintenance-badge ${isActive ? 'maintenance-badge-active' : 'maintenance-badge-inactive'}`}>
              {isActive ? 'ACTIVE' : 'INACTIVE'}
            </span>
          </div>

          {maintenance ? (
            <div className="maintenance-details">
              <div className="maintenance-detail-row">
                <span className="maintenance-detail-label">Start Time</span>
                <span className="maintenance-detail-value">{formatTimestamp(maintenance.startTime)}</span>
              </div>
              <div className="maintenance-detail-row">
                <span className="maintenance-detail-label">Scheduled End</span>
                <span className="maintenance-detail-value">{formatTimestamp(maintenance.endTime)}</span>
              </div>
              <div className="maintenance-detail-row">
                <span className="maintenance-detail-label">Created By</span>
                <span className="maintenance-detail-value">{maintenance.createdBy || '—'}</span>
              </div>
              {maintenance.endedEarly && (
                <div className="maintenance-detail-row">
                  <span className="maintenance-detail-label">Ended Early At</span>
                  <span className="maintenance-detail-value">{formatTimestamp(maintenance.endedAt)}</span>
                </div>
              )}
              <div className="maintenance-detail-row">
                <span className="maintenance-detail-label">Pre-Maintenance (EN)</span>
                <span className="maintenance-detail-value">{maintenance.preMessage?.en || '—'}</span>
              </div>
              <div className="maintenance-detail-row">
                <span className="maintenance-detail-label">Pre-Maintenance (FR)</span>
                <span className="maintenance-detail-value">{maintenance.preMessage?.fr || '—'}</span>
              </div>
              <div className="maintenance-detail-row">
                <span className="maintenance-detail-label">During Maintenance (EN)</span>
                <span className="maintenance-detail-value">{maintenance.message?.en || '—'}</span>
              </div>
              <div className="maintenance-detail-row">
                <span className="maintenance-detail-label">During Maintenance (FR)</span>
                <span className="maintenance-detail-value">{maintenance.message?.fr || '—'}</span>
              </div>
            </div>
          ) : (
            <p className="maintenance-no-data">No maintenance has been scheduled yet.</p>
          )}

          <div className="maintenance-actions">
            {isActive ? (
              <button onClick={handleEndMaintenance} disabled={saving} className="btn btn-danger">
                {saving ? 'Ending...' : 'End Maintenance'}
              </button>
            ) : (
              <button onClick={() => { setShowForm(true); setForm((prev) => ({ ...prev, startTime: dayjs() })); }} className="btn btn-primary">
                Enable Maintenance
              </button>
            )}
          </div>
        </div>

        {/* Confirm End Maintenance Modal */}
        {showConfirmModal && (
          <div className="confirm-modal-overlay" onClick={() => setShowConfirmModal(false)}>
            <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
              <h3 className="confirm-modal-title">End Maintenance</h3>
              <p className="confirm-modal-message">
                Are you sure you want to end maintenance now? This will restore service for all users immediately.
              </p>
              <div className="confirm-modal-actions">
                <button onClick={() => setShowConfirmModal(false)} className="btn btn-secondary">
                  Cancel
                </button>
                <button onClick={confirmEndMaintenance} className="btn btn-danger">
                  End Maintenance
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Enable Maintenance Form */}
        {showForm && !isActive && (
          <div className="settings-card maintenance-form-card">
            <h3 className="card-title">Schedule Maintenance</h3>
            <div className="form-fields">
              <h4 className="form-section-title">Pre-Maintenance Message</h4>
              <p className="form-section-hint">Shown to users before the maintenance window starts.</p>
              <div className="form-group">
                <label className="form-label">Pre-Maintenance (English)</label>
                <textarea
                  value={form.preMessageEn}
                  onChange={(e) => handleFormChange('preMessageEn', e.target.value)}
                  className="form-input form-textarea"
                  rows={3}
                  placeholder="Enter pre-maintenance message in English..."
                />
              </div>
              <div className="form-group">
                <label className="form-label">Pre-Maintenance (French)</label>
                <textarea
                  value={form.preMessageFr}
                  onChange={(e) => handleFormChange('preMessageFr', e.target.value)}
                  className="form-input form-textarea"
                  rows={3}
                  placeholder="Entrez le message de pré-maintenance en français..."
                />
              </div>

              <h4 className="form-section-title">During Maintenance Message</h4>
              <p className="form-section-hint">Shown to users once the maintenance window has started.</p>
              <div className="form-group">
                <label className="form-label">During Maintenance (English)</label>
                <textarea
                  value={form.messageEn}
                  onChange={(e) => handleFormChange('messageEn', e.target.value)}
                  className="form-input form-textarea"
                  rows={3}
                  placeholder="Enter maintenance message in English..."
                />
              </div>
              <div className="form-group">
                <label className="form-label">During Maintenance (French)</label>
                <textarea
                  value={form.messageFr}
                  onChange={(e) => handleFormChange('messageFr', e.target.value)}
                  className="form-input form-textarea"
                  rows={3}
                  placeholder="Entrez le message de maintenance en français..."
                />
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Start Time</label>
                  <DateTimePicker
                    value={form.startTime}
                    onChange={(val) => handleFormChange('startTime', val)}
                    format={DATE_FORMAT}
                    ampm={false}
                    slotProps={pickerSlotProps}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">End Time</label>
                  <DateTimePicker
                    value={form.endTime}
                    onChange={(val) => handleFormChange('endTime', val)}
                    format={DATE_FORMAT}
                    ampm={false}
                    minDateTime={form.startTime}
                    slotProps={pickerSlotProps}
                  />
                </div>
              </div>
              <div className="maintenance-form-actions">
                <button onClick={() => setShowForm(false)} className="btn btn-secondary">
                  Cancel
                </button>
                <button onClick={handleEnableMaintenance} disabled={saving} className="btn btn-primary">
                  {saving ? 'Enabling...' : 'Enable Maintenance'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </LocalizationProvider>
  );
};

export default Maintenance;
